import React, { useEffect, useState } from 'react';
import {
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  Spinner,
  NavbarMenuToggle,
  NavbarMenu,
  NavbarMenuItem,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from "@heroui/react";
import { useTranslation } from 'react-i18next';
import { UserAvatar } from '~/components/atoms/UserAvatar';
import { BaseLink } from '~/components/atoms/Link';
import { Logo } from '~/components/atoms/Logo';
import { ROUTES } from '~/constants/routes.contants';
import { useRouter } from 'next/router';
import { FiChevronDown, FiHelpCircle } from 'react-icons/fi';
import { FaPencilAlt, FaApple, FaAndroid, FaHome, FaSearch, FaCamera, FaMap, FaUser } from 'react-icons/fa';
import { PERIOD } from '~/constants/period.constants';
import { useAuthModal } from '~/hooks/useAuthModal';
import ReviewModal from '~/components/molecules/ReviewModal';
import NavbarNotificationComponent from '~/components/molecules/NavbarNotificationComponent';
import useUserContext from '~/hooks/useUserContext';
import { getPlatform, isSSR } from '~/utils/client-utils';
import { useEvent } from '~/hooks/useEvent';

type NavigationItem = {
  key: string;
  href: string;
  label: string;
};

type NavigationSection = {
  discover: NavigationItem[];
  popular: NavigationItem[];
  community: NavigationItem[];
  support: NavigationItem[];
};

const NAVIGATION_ITEMS: NavigationSection = {
  discover: [
    { key: 'places-near-me', href: ROUTES.PLACES, label: 'navigation.placesNearMe' },
    { key: 'foods-near-me', href: ROUTES.FOODS, label: 'navigation.foodsNearMe' },
    { key: 'categories', href: ROUTES.CATEGORIES, label: 'categories.title' },
  ],
  popular: [
    {
      key: 'popular-foods-month',
      href: `${ROUTES.FOODS}?period=${PERIOD.LAST_MONTH}`,
      label: 'navigation.popularFoodsMonth'
    },
    {
      key: 'popular-places-month',
      href: `${ROUTES.PLACES}?period=${PERIOD.LAST_MONTH}&sort=rating-desc`,
      label: 'navigation.popularPlacesMonth'
    },
    {
      key: 'most-reviewed-places-month',
      href: `${ROUTES.PLACES}?period=${PERIOD.LAST_MONTH}&sort=reviewCount-desc`,
      label: 'navigation.mostReviewedPlacesMonth'
    },
  ],
  community: [
    { key: 'feed', href: ROUTES.HOME, label: 'navigation.discover' },
    { key: 'my-profile', href: ROUTES.AUTH.PROFILE, label: 'navigation.myProfile' },
    { key: 'gourmets', href: ROUTES.USERS, label: 'navigation.gourmets' },
  ],
  support: [
    { key: 'contact', href: '/contact', label: 'navigation.contact' },
    { key: 'privacy', href: '/privacy', label: 'navigation.privacy' },
    { key: 'terms', href: '/terms', label: 'navigation.terms' },
    { key: 'language', href: ROUTES.LANGUAGE, label: 'language.select' },
  ],
};

export default function Navigation() {
  const { t } = useTranslation();
  const { user, loading } = useUserContext();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [platform, setPlatform] = useState<string | null>(null);
  const router = useRouter();
  const { requireAuth } = useAuthModal();
  const { sendEvent } = useEvent();

  useEffect(() => {
    setIsMenuOpen(false);
  }, [router.pathname]);

  useEffect(() => {
    setPlatform(getPlatform());
  }, []);

  const handleReviewClick = () => {
    sendEvent('add_review_navigation_button_click');
    if (!user) {
      requireAuth(() => setIsReviewModalOpen(true));
      return;
    }
    setIsReviewModalOpen(true);
  };

  const handleIosAppBannerClick = () => {
    sendEvent('ios_app_banner_click');
    window.open('https://apps.apple.com/app/bitecritic/id6743010069', '_blank');
  };

  const handleAndroidAppBannerClick = () => {
    sendEvent('android_app_banner_click');
    window.open('https://play.google.com/store/apps/details?id=4974356512198139704', '_blank');
  };

  const renderDropdownItems = (items: NavigationItem[]) => (
    items.map(({ key, href, label }) => (
      <DropdownItem key={key}>
        <BaseLink href={href} className="w-full">
          {t(label)}
        </BaseLink>
      </DropdownItem>
    ))
  );

  const renderMobileMenuItems = (
    title: string,
    items: NavigationItem[],
  ) => (
    <NavbarMenuItem className="mb-4">
      <p className="text-sm font-semibold text-gray-500 mb-2">{t(title)}</p>
      <div className="flex flex-col gap-2">
        {items.map(({ key, href, label }) => (
          <BaseLink key={key} href={href} className="w-full">
            {t(label)}
          </BaseLink>
        ))}
      </div>
    </NavbarMenuItem>
  );

  const isNativeAndroid = platform === 'android_app';
  const isNativeIos = platform === 'ios_app';

  // Check if the current path matches a specific route
  const isActive = (path: string) => {
    if (path === ROUTES.HOME) {
      return router.pathname === ROUTES.HOME;
    }
    return router.pathname.startsWith(path);
  };

  // Get the profile route based on user status
  const getProfileRoute = () => {
    if (user) {
      return `${ROUTES.USERS}/${user.username}`;
    }
    return ROUTES.AUTH.LOGIN;
  };

  return (
    <>
      <Navbar
        onMenuOpenChange={setIsMenuOpen}
        isMenuOpen={isMenuOpen}
        maxWidth={'full'}
        className={`fixed ${isMenuOpen && (isNativeAndroid) ? 'menu-open' : ''}`}
      >
        <NavbarContent>
          <NavbarMenuToggle
            aria-label={isMenuOpen ? (t('closeMenu') || '') : (t('openMenu') || '')}
            className="md:hidden"
          />
          <NavbarBrand>
            <BaseLink href={ROUTES.HOME}>
              <Logo variant="text" />
            </BaseLink>
          </NavbarBrand>
        </NavbarContent>

        <NavbarContent className="hidden sm:flex gap-4" justify="center">
          {/* Discover Dropdown */}
          <NavbarItem>
            <Dropdown>
              <DropdownTrigger>
                <Button
                  variant="light"
                  endContent={<FiChevronDown />}
                >
                  {t('navigation.discover')}
                </Button>
              </DropdownTrigger>
              <DropdownMenu>
                {renderDropdownItems(NAVIGATION_ITEMS.discover)}
              </DropdownMenu>
            </Dropdown>
          </NavbarItem>

          {/* Community Dropdown */}
          <NavbarItem>
            <Dropdown>
              <DropdownTrigger>
                <Button
                  variant="light"
                  endContent={<FiChevronDown />}
                >
                  {t('navigation.community')}
                </Button>
              </DropdownTrigger>
              <DropdownMenu>
                {renderDropdownItems(NAVIGATION_ITEMS.community)}
              </DropdownMenu>
            </Dropdown>
          </NavbarItem>

          {/* Support Dropdown */}
          <NavbarItem>
            <Dropdown>
              <DropdownTrigger>
                <Button
                  variant="light"
                  endContent={<FiChevronDown />}
                  startContent={<FiHelpCircle />}
                >
                  {t('navigation.support')}
                </Button>
              </DropdownTrigger>
              <DropdownMenu>
                {renderDropdownItems(NAVIGATION_ITEMS.support)}
              </DropdownMenu>
            </Dropdown>
          </NavbarItem>
        </NavbarContent>

        <NavbarContent justify="end" className="gap-2">
          <NavbarItem>
            <NavbarNotificationComponent />
          </NavbarItem>
          <NavbarItem className="hidden md:flex">
            <Button
              isIconOnly
              color="secondary"
              variant="solid"
              onPress={handleReviewClick}
              aria-label={String(t('add-review'))}
            >
              <FaPencilAlt />
            </Button>
          </NavbarItem>
          {user ? (
            <>
              <NavbarItem className="flex md:flex items-center justify-center hidden md:block">
                <BaseLink href={`${ROUTES.USERS}/${user.username}`}>
                  <UserAvatar user={user} />
                </BaseLink>
              </NavbarItem>
            </>
          ) : (
            <>
              {loading ? (
                <NavbarItem className="flex items-center justify-center w-10 h-10">
                  <Spinner size="md" />
                </NavbarItem>
              ) : (
                <NavbarItem className="lg:flex gap-2">
                  <Button
                    as={BaseLink}
                    href={ROUTES.AUTH.SIGNUP}
                    color="primary"
                    onPress={() => sendEvent('navigation_signup_button_click')}
                  >
                    {t('auth.signup')}
                  </Button>
                </NavbarItem>
              )}
            </>
          )}
        </NavbarContent>

        {/* Mobile Menu */}
        <NavbarMenu className={`${isMenuOpen && (isNativeAndroid) ? 'menu-open' : ''}`}>
          {renderMobileMenuItems('navigation.discover', NAVIGATION_ITEMS.discover)}
          {renderMobileMenuItems('navigation.popular', NAVIGATION_ITEMS.popular)}
          {renderMobileMenuItems('navigation.community', NAVIGATION_ITEMS.community)}
          {renderMobileMenuItems('navigation.support', NAVIGATION_ITEMS.support)}

          {/* Settings Section */}
          <NavbarMenuItem className="mt-6 border-t pt-6">
            <div className="flex flex-col gap-2">
              <p className="text-sm font-semibold text-gray-500">{t('settings.title')}</p>
              {user && (
                <div className="flex items-center gap-2 mb-2">
                  <NavbarNotificationComponent />
                  <span>{t('notifications.title')}</span>
                </div>
              )}
            </div>
          </NavbarMenuItem>
          <NavbarMenuItem className="flex justify-start mt-2 mb-32">
            <div className="w-full">
              {(!isNativeAndroid && !isNativeIos) && (
                <>
                  <p className="text-sm font-semibold text-gray-500 mb-2">{t('app.actions')}</p>
                  {platform === 'web' && (
                    <Button
                      variant="solid"
                      size="sm"
                      className="w-full sm:w-auto mb-2"
                      onPress={handleIosAppBannerClick}
                    >
                      <FaApple className="mr-1" />
                      {t('download.ios_app')}
                    </Button>
                  )}
                  {platform === 'web' && (
                    <Button
                      variant="solid"
                      size="sm"
                      className="w-full sm:w-auto"
                      onPress={handleAndroidAppBannerClick}
                    >
                      <FaAndroid className="mr-1" />
                      {t('download.android_app')}
                    </Button>
                  )}
                </>
              )}
            </div>
          </NavbarMenuItem>
        </NavbarMenu>
      </Navbar>

      {/* Mobile Tapbar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 md:hidden">
        <div className="flex justify-around items-center h-16">
          <BaseLink
            title={t('navigation.home') || 'home'}
            href={ROUTES.HOME} 
            className={`flex items-center justify-center p-2 ${isActive(ROUTES.HOME) ? 'text-primary' : 'text-gray-500'}`}
          >
            <FaHome className="text-3xl" />
          </BaseLink>
          
          <BaseLink 
            title={t('navigation.search') || 'search'}
            href={ROUTES.FOODS} 
            className={`flex items-center justify-center p-2 ${isActive(ROUTES.FOODS) ? 'text-primary' : 'text-gray-500'}`}
          >
            <FaSearch className="text-2xl" />
          </BaseLink>
          
          <button
            title={t('navigation.add-review') || 'add-review'}
            onClick={handleReviewClick}
            className="flex items-center justify-center relative"
            aria-label={String(t('add-review'))}
          >
            <div className="bg-secondary text-white rounded-full p-4">
              <FaCamera className="text-xl" />
            </div>
          </button>
          
          <BaseLink 
            title={t('navigation.places') || 'places'}
            href={ROUTES.PLACES} 
            className={`flex items-center justify-center p-2 ${isActive(ROUTES.PLACES) ? 'text-primary' : 'text-gray-500'}`}
          >
            <FaMap className="text-2xl" />
          </BaseLink>
          
          <BaseLink 
            title={t('navigation.profile') || 'profile'}
            href={getProfileRoute()} 
            className={`flex items-center justify-center p-2 ${isActive(ROUTES.USERS) || isActive(ROUTES.AUTH.PROFILE) ? 'text-primary' : 'text-gray-500'}`}
          >
            <div className="w-8 h-8 flex items-center justify-center">
              {user ? (
                <UserAvatar user={user} size="sm" />
              ) : (
                <FaUser className="text-2xl" />
              )}
            </div>
          </BaseLink>
        </div>
      </div>

      {!isSSR && (
        <ReviewModal
          isOpen={isReviewModalOpen}
          onClose={() => setIsReviewModalOpen(false)}
        />
      )}
    </>
  );
}
